/**
 * Refonte WP-66 (M-29; D-29, CP §15.5, §15.8, §15.21-3; A-05 superseded) — the destination of ONE purchase
 * line in S1 / S3 edit mode: the pure model of the « Magasin de la ligne » selector next to T.R, of the
 * lines table's mark and of the D5 summary. No I/O, no React.
 *
 *   saisie.destination — null = the facture's magasin (the header default of every line), or the line's
 *     own { magasin_id, magasin_nom, emplacement_id, emplacement_code }: a magasin of the officine and,
 *     optionally, an emplacement of THAT magasin. An emplacement picked while the line follows the
 *     facture's magasin is stored with that magasin named (an emplacement belongs to a magasin — the
 *     server refuses one without it).
 *
 * The server stays the authority (422 / 404, services/achat-destination-regles.js): the selector only
 * offers the officine's active magasins and the chosen magasin's own emplacements.
 */
import { DESTINATION_S1 } from "@/config/achat.messages.js";

const texte = (v) => (v == null ? "" : String(v));

/** A line of `GET /achats/:id` → its destination (null = the facture's magasin). */
export function destinationDepuisServeur(d) {
  if (!d?.magasin_id) return null;
  return {
    magasin_id: d.magasin_id,
    magasin_nom: d.magasin?.nom ?? null,
    emplacement_id: d.emplacement_id ?? null,
    emplacement_code: d.emplacement?.code ?? null,
  };
}

/** What a line sends (the full-line vocabulary of S1 / S3: the pair, null = the facture's magasin). */
export const destinationVersServeur = (l) => ({
  magasin_id: l?.destination?.magasin_id ?? null,
  emplacement_id: l?.destination?.emplacement_id ?? null,
});

/** The line names its own magasin. */
export const destinationPropre = (l) => Boolean(l?.destination?.magasin_id);

/** « Réserve · E-05 », « Réserve » — the line's own destination in words (null when it has none). */
export function libelleDestination(dest) {
  if (!dest?.magasin_id) return null;
  const nom = texte(dest.magasin_nom) || DESTINATION_S1.magasinSansNom;
  return dest.emplacement_code ? `${nom} · ${dest.emplacement_code}` : nom;
}

/**
 * The facture's magasin as the lines default to it: the header's, else the principal (A-05: a facture
 * without magasin enters the principal) — { id, nom } or null while the magasins are unknown.
 */
export function magasinDeLaFacture(magasinId, magasins = []) {
  const liste = magasins ?? [];
  const m = (magasinId && liste.find((x) => x.id === magasinId)) || liste.find((x) => x.is_principal) || null;
  return m ? { id: m.id, nom: m.nom } : null;
}

/** Two destinations name the same place (null = the facture's magasin). */
export const memeDestination = (a, b) =>
  (a?.magasin_id ?? null) === (b?.magasin_id ?? null) && (a?.emplacement_id ?? null) === (b?.emplacement_id ?? null);

/** The entry with this destination (the same object when nothing changes). */
export function changerDestination(s, destination) {
  const d = destination?.magasin_id ? destination : null;
  if (memeDestination(s?.destination, d)) return s;
  return { ...s, destination: d };
}

// ─── The selector's dialog state ─────────────────────────────────────────────

/** The dialog's state from an entry: magasin "" = the facture's magasin. */
export function etatDestination(s) {
  const d = s?.destination;
  return {
    magasin_id: d?.magasin_id ?? "",
    emplacement_id: d?.emplacement_id ?? null,
    emplacement_code: d?.emplacement_code ?? null,
  };
}

/** The magasin whose emplacements the dialog lists: the one chosen, else the facture's. */
export const magasinDesEmplacements = (etat, magasinFacture) => etat?.magasin_id || magasinFacture?.id || null;

/** A magasin chosen in the dialog: its emplacement goes (an emplacement belongs to its magasin). */
export function choisirMagasin(etat, magasin_id) {
  const id = magasin_id ?? "";
  if (id === (etat?.magasin_id ?? "")) return etat;
  return { magasin_id: id, emplacement_id: null, emplacement_code: null };
}

/** An emplacement chosen (or cleared) in the dialog. */
export const choisirEmplacement = (etat, option) => ({
  ...etat,
  emplacement_id: option?.value ?? null,
  emplacement_code: option?.label ?? null,
});

/**
 * The magasin options: « Magasin de la facture — Principal » first (value ""), then the officine's
 * active magasins (the principal named as such).
 */
export function optionsMagasins(magasins = [], magasinFacture = null) {
  return [
    { value: "", label: DESTINATION_S1.magasinFacture(magasinFacture?.nom ?? null) },
    ...(magasins ?? [])
      .filter((m) => !m.statut || m.statut === "ACTIF")
      .map((m) => ({ value: m.id, label: m.is_principal ? DESTINATION_S1.principal(m.nom) : m.nom })),
  ];
}

/**
 * The destination the dialog applies: null when it follows the facture's magasin without emplacement;
 * an emplacement of the facture's magasin names that magasin.
 */
export function destinationChoisie(etat, { magasins = [], magasinFacture = null } = {}) {
  const magasin_id = etat?.magasin_id || (etat?.emplacement_id ? magasinFacture?.id ?? null : null);
  if (!magasin_id) return null;
  const m = (magasins ?? []).find((x) => x.id === magasin_id);
  return {
    magasin_id,
    magasin_nom: m?.nom ?? (magasin_id === magasinFacture?.id ? magasinFacture?.nom ?? null : null),
    emplacement_id: etat.emplacement_id ?? null,
    emplacement_code: etat.emplacement_id ? etat.emplacement_code ?? null : null,
  };
}

/** The selector button's accessible name and the band's note when the line has its own destination. */
export function texteBoutonDestination(s, magasinFacture) {
  const propre = libelleDestination(s?.destination);
  return DESTINATION_S1.bouton(propre ?? DESTINATION_S1.magasinFacture(magasinFacture?.nom ?? null));
}

export function noteDestination(s, magasinFacture) {
  const propre = libelleDestination(s?.destination);
  return propre ? DESTINATION_S1.note(propre, magasinFacture?.nom ?? null) : null;
}

// ─── The D5 summary: where the units enter ───────────────────────────────────

/**
 * The units entering stock per magasin, in the lines' order of first appearance (the facture's magasin
 * first): [{ magasin_id, nom, unites }]. `unitesDe(l)` = the units a line enters (the engine's).
 */
export function repartitionParMagasin(lignes, { magasinFacture = null, unitesDe }) {
  const parMagasin = new Map();
  const ajouter = (id, nom, n) => {
    const r = parMagasin.get(id) ?? { magasin_id: id, nom, unites: 0 };
    r.unites += n;
    parMagasin.set(id, r);
  };
  if (magasinFacture) parMagasin.set(magasinFacture.id, { magasin_id: magasinFacture.id, nom: magasinFacture.nom, unites: 0 });
  for (const l of lignes ?? []) {
    const n = unitesDe(l) ?? 0;
    if (destinationPropre(l)) ajouter(l.destination.magasin_id, l.destination.magasin_nom, n);
    else ajouter(magasinFacture?.id ?? null, magasinFacture?.nom ?? null, n);
  }
  return [...parMagasin.values()].filter((r) => r.unites > 0);
}

/** « Principal 30, Réserve 7 » when the units enter several magasins; the one name otherwise. */
export function texteRepartition(repartition, magasinFacture = null) {
  if (!repartition?.length) return magasinFacture?.nom ?? null;
  if (repartition.length === 1) return repartition[0].nom ?? magasinFacture?.nom ?? null;
  return repartition.map((r) => DESTINATION_S1.partMagasin(r.nom, r.unites)).join(", ");
}
